/**
 * Social OAuth utilities for Google, Apple, and Facebook login.
 * Uses popup-based OAuth flows. Ported from mn-project-tool (Siembra).
 */

export interface OAuthConfig {
  microsoft: { enabled: boolean; clientId: string; tenantId: string; redirectUri: string };
  google: { enabled: boolean; clientId: string };
  apple: { enabled: boolean; clientId: string };
  facebook: { enabled: boolean; clientId: string };
  password?: { enabled: boolean; allowRegistration: boolean; allowAccessCodeRegistration: boolean };
}

export interface OAuthResult {
  provider: 'google' | 'apple' | 'facebook';
  token: string;
  email?: string;
  name?: string;
}

function openPopup(url: string, name: string, width = 500, height = 600): Window | null {
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  return window.open(
    url,
    name,
    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`,
  );
}

function generateState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function generatePKCE(): Promise<{ verifier: string; challenge: string }> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const verifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const challenge = btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return { verifier, challenge };
}

const OAUTH_STATE_KEY = 'oauth_state';
const OAUTH_VERIFIER_KEY = 'oauth_verifier';
const OAUTH_PROVIDER_KEY = 'oauth_provider';

export async function loginWithGoogle(clientId: string): Promise<OAuthResult> {
  const state = generateState();
  const { verifier, challenge } = await generatePKCE();
  const redirectUri = `${window.location.origin}/oauth/callback`;
  const nonce = generateState();

  sessionStorage.setItem(OAUTH_STATE_KEY, state);
  sessionStorage.setItem(OAUTH_VERIFIER_KEY, verifier);
  sessionStorage.setItem(OAUTH_PROVIDER_KEY, 'google');

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    prompt: 'select_account',
  });

  const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;

  return new Promise((resolve, reject) => {
    const popup = openPopup(authUrl, 'google-login');
    if (!popup) {
      reject(new Error('Failed to open popup window'));
      return;
    }

    const messageHandler = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'oauth-callback') return;

      window.removeEventListener('message', messageHandler);
      clearInterval(pollInterval);

      const { code, state: returnedState, error } = event.data;
      if (error) {
        reject(new Error(error));
        return;
      }

      const savedState = sessionStorage.getItem(OAUTH_STATE_KEY);
      if (returnedState !== savedState) {
        reject(new Error('OAuth state mismatch'));
        return;
      }

      try {
        const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            client_id: clientId,
            code,
            code_verifier: verifier,
            grant_type: 'authorization_code',
            redirect_uri: redirectUri,
          }),
        });

        if (!tokenResponse.ok) {
          throw new Error('Failed to exchange code for tokens');
        }

        const tokens = await tokenResponse.json();
        const idToken = tokens.id_token;
        const payload = JSON.parse(atob(idToken.split('.')[1]));

        resolve({ provider: 'google', token: idToken, email: payload.email, name: payload.name });
      } catch (err) {
        reject(err);
      } finally {
        sessionStorage.removeItem(OAUTH_STATE_KEY);
        sessionStorage.removeItem(OAUTH_VERIFIER_KEY);
        sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
      }
    };

    window.addEventListener('message', messageHandler);

    const pollInterval = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollInterval);
        window.removeEventListener('message', messageHandler);
        reject(new Error('Popup was closed'));
      }
    }, 500);
  });
}

export async function loginWithApple(clientId: string): Promise<OAuthResult> {
  const state = generateState();
  const nonce = generateState();
  const redirectUri = `${window.location.origin}/oauth/callback`;

  sessionStorage.setItem(OAUTH_STATE_KEY, state);
  sessionStorage.setItem(OAUTH_PROVIDER_KEY, 'apple');

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code id_token',
    response_mode: 'fragment',
    scope: 'name email',
    state,
    nonce,
  });

  const authUrl = `https://appleid.apple.com/auth/authorize?${params}`;

  return new Promise((resolve, reject) => {
    const popup = openPopup(authUrl, 'apple-login');
    if (!popup) {
      reject(new Error('Failed to open popup window'));
      return;
    }

    const messageHandler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'oauth-callback') return;

      window.removeEventListener('message', messageHandler);
      clearInterval(pollInterval);

      const { id_token, state: returnedState, error, user } = event.data;
      if (error) {
        reject(new Error(error));
        return;
      }

      const savedState = sessionStorage.getItem(OAUTH_STATE_KEY);
      if (returnedState !== savedState) {
        reject(new Error('OAuth state mismatch'));
        return;
      }

      try {
        const payload = JSON.parse(atob(id_token.split('.')[1]));
        let name = '';
        if (user) {
          const userInfo = typeof user === 'string' ? JSON.parse(user) : user;
          name = [userInfo.name?.firstName, userInfo.name?.lastName].filter(Boolean).join(' ');
        }
        resolve({ provider: 'apple', token: id_token, email: payload.email, name: name || undefined });
      } catch (err) {
        reject(err);
      } finally {
        sessionStorage.removeItem(OAUTH_STATE_KEY);
        sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
      }
    };

    window.addEventListener('message', messageHandler);

    const pollInterval = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollInterval);
        window.removeEventListener('message', messageHandler);
        reject(new Error('Popup was closed'));
      }
    }, 500);
  });
}

export async function loginWithFacebook(clientId: string): Promise<OAuthResult> {
  const state = generateState();
  const redirectUri = `${window.location.origin}/oauth/callback`;

  sessionStorage.setItem(OAUTH_STATE_KEY, state);
  sessionStorage.setItem(OAUTH_PROVIDER_KEY, 'facebook');

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'token',
    scope: 'email public_profile',
    state,
  });

  const authUrl = `https://www.facebook.com/v18.0/dialog/oauth?${params}`;

  return new Promise((resolve, reject) => {
    const popup = openPopup(authUrl, 'facebook-login');
    if (!popup) {
      reject(new Error('Failed to open popup window'));
      return;
    }

    const messageHandler = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'oauth-callback') return;

      window.removeEventListener('message', messageHandler);
      clearInterval(pollInterval);

      const { access_token, state: returnedState, error } = event.data;
      if (error) {
        reject(new Error(error));
        return;
      }

      const savedState = sessionStorage.getItem(OAUTH_STATE_KEY);
      if (returnedState !== savedState) {
        reject(new Error('OAuth state mismatch'));
        return;
      }

      try {
        const userResponse = await fetch(
          `https://graph.facebook.com/me?fields=id,email,name&access_token=${access_token}`,
        );
        if (!userResponse.ok) {
          throw new Error('Failed to fetch Facebook user info');
        }
        const userData = await userResponse.json();
        resolve({ provider: 'facebook', token: access_token, email: userData.email, name: userData.name });
      } catch (err) {
        reject(err);
      } finally {
        sessionStorage.removeItem(OAUTH_STATE_KEY);
        sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
      }
    };

    window.addEventListener('message', messageHandler);

    const pollInterval = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollInterval);
        window.removeEventListener('message', messageHandler);
        reject(new Error('Popup was closed'));
      }
    }, 500);
  });
}
